import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { loadGoogleMapsApi } from './google-maps-loader';

export interface EventsFilters {
  when: string;
  time: string[];
  rollWith: string[];
  categorySlugs: string[];
  locationModes: string[];
  duration: string[];
  priceMin: number;
  priceMax: number;
  popular: boolean;
}

export interface EventResult {
  id: string;
  title: string;
  place: string;
  description: string;
  photos: string[];
  distanceMiles: number;
  travelMinutes: number;
  commitmentLevel: 'easy' | 'moderate' | 'high';
  price: number;
  petFriendly: boolean;
  familyFriendly?: boolean;
  sober?: boolean;
  rating: number;
  callout: string;
  source: 'popularSeed' | 'ai';
  category?: 'creative' | 'entertainment' | 'exercise';
  duration?: string;
  timeOfDay?: string;
  date?: string;
  time?: string;
  eventDetails?: string;
  hasExactDetails?: boolean;
  isUpcomingEvent?: boolean;
  datetime?: string;
  sourceUrl?: string;
  mapsUrl?: string;
  searchUrl?: string;
  tags?: string[];
  address?: string;
  sourcePhotoUrl?: string;
  lat?: number;
  lng?: number;
}

interface EventsPaging {
  offset: number;
  limit: number;
}

export interface PopularActivitiesResult {
  creative: string[];
  entertainment: string[];
  exercise: string[];
  all: string[];
}

export interface NearbyActivitiesParams {
  locationName?: string;
  lat?: number;
  lng?: number;
  category?: 'creative' | 'entertainment' | 'exercise' | null;
  searchQuery?: string;
  activities?: string[];
  scope?: 'all' | 'my';
  limit?: number;
  excludeTitles?: string[];
}

@Injectable({ providedIn: 'root' })
export class EventsService {
  private readonly geminiBackendUrl = `${environment.apiUrl}api/v1/ai-events/generate/`;

  private savedEvents: EventResult[] = [];
  private trashedEventIds = new Set<string>();
  private trashedEventsMap = new Map<string, EventResult>();

  savedEventsCount$ = new BehaviorSubject<number>(0);
  trashedEventsCount$ = new BehaviorSubject<number>(0);

  private googleCoordsCache = new Map<string, { lat: number; lng: number }>();
  private inFlightGeocode = new Map<string, Promise<{ lat: number; lng: number } | null>>();
  private geocodeQueue: Array<() => void> = [];
  private activeGeocodeCount = 0;
  private readonly MAX_CONCURRENT_GEOCODES = 6;
  private placesDummyDiv: HTMLDivElement | null = null;

  constructor(private http: HttpClient) {
    this.initInteractions();
  }

  private normalizeGeocodeKey(query: string): string {
    return (query || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  public getCachedGoogleCoords(query: string): { lat: number; lng: number } | null {
    const norm = this.normalizeGeocodeKey(query);
    if (!norm) return null;

    if (this.googleCoordsCache.has(norm)) {
      return this.googleCoordsCache.get(norm)!;
    }

    if (typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem(`a8_gcoords_v2_${norm}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number' && !isNaN(parsed.lat) && !isNaN(parsed.lng) && (parsed.lat !== 0 || parsed.lng !== 0)) {
            const coords = { lat: parsed.lat, lng: parsed.lng };
            this.googleCoordsCache.set(norm, coords);
            return coords;
          }
        }
      } catch {}
    }
    return null;
  }

  private saveGoogleCoordsToCache(query: string, coords: { lat: number; lng: number }) {
    const norm = this.normalizeGeocodeKey(query);
    if (!norm || !coords || isNaN(coords.lat) || isNaN(coords.lng) || (coords.lat === 0 && coords.lng === 0)) {
      return;
    }
    this.googleCoordsCache.set(norm, coords);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(`a8_gcoords_v2_${norm}`, JSON.stringify(coords));
      } catch {}
    }
  }

  private enqueueGeocodeTask<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = () => {
        this.activeGeocodeCount++;
        task()
          .then(resolve, reject)
          .finally(() => {
            setTimeout(() => {
              this.activeGeocodeCount = Math.max(0, this.activeGeocodeCount - 1);
              const next = this.geocodeQueue.shift();
              if (next) next();
            }, 30);
          });
      };

      if (this.activeGeocodeCount < this.MAX_CONCURRENT_GEOCODES) {
        run();
      } else {
        this.geocodeQueue.push(run);
      }
    });
  }

  public resolveCoordsFromGoogle(
    query: string,
    biasCenter?: { lat: number; lng: number }
  ): Promise<{ lat: number; lng: number } | null> {
    const cleanedQuery = (query || '').replace(/\s+-\s+/g, ', ').replace(/\s+/g, ' ').trim();
    const norm = this.normalizeGeocodeKey(cleanedQuery);
    if (!norm || norm.length < 3) {
      return Promise.resolve(null);
    }

    const cached = this.getCachedGoogleCoords(cleanedQuery);
    if (cached) {
      return Promise.resolve(cached);
    }

    if (this.inFlightGeocode.has(norm)) {
      return this.inFlightGeocode.get(norm)!;
    }

    const promise = this.enqueueGeocodeTask(async () => {
      try {
        if (typeof window !== 'undefined' && environment.googleMapsApiKey) {
          await loadGoogleMapsApi(environment.googleMapsApiKey)();
        }

        const gMaps = typeof window !== 'undefined' ? (window as any).google?.maps : null;
        if (gMaps) {
          const coords = await this.queryGoogleMapsJsSdk(gMaps, cleanedQuery, biasCenter);
          if (coords) {
            this.saveGoogleCoordsToCache(cleanedQuery, coords);
            return coords;
          }
        }

        if (environment.googleMapsApiKey) {
          const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(cleanedQuery)}&key=${encodeURIComponent(environment.googleMapsApiKey)}`;
          const resp: any = await this.http.get(url).pipe(catchError(() => of(null))).toPromise();
          const loc = resp?.results?.[0]?.geometry?.location;
          if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
            const coords = { lat: Number(loc.lat), lng: Number(loc.lng) };
            this.saveGoogleCoordsToCache(cleanedQuery, coords);
            return coords;
          }
        }
      } catch (err) {
        console.warn('[EventsService] Google coordinate lookup error:', err);
      }
      return null;
    }).finally(() => {
      this.inFlightGeocode.delete(norm);
    });

    this.inFlightGeocode.set(norm, promise);
    return promise;
  }

  private queryGoogleMapsJsSdk(
    gMaps: any,
    query: string,
    biasCenter?: { lat: number; lng: number }
  ): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
      const tryGeocoder = (fallbackCoords: { lat: number; lng: number } | null = null) => {
        if (!gMaps.Geocoder) {
          resolve(fallbackCoords);
          return;
        }
        try {
          const geocoder = new gMaps.Geocoder();
          const req: any = { address: query };
          if (biasCenter && biasCenter.lat && biasCenter.lng && gMaps.LatLngBounds && gMaps.LatLng) {
            const delta = 0.25;
            req.bounds = new gMaps.LatLngBounds(
              new gMaps.LatLng(biasCenter.lat - delta, biasCenter.lng - delta),
              new gMaps.LatLng(biasCenter.lat + delta, biasCenter.lng + delta)
            );
          }
          geocoder.geocode(req, (results: any[], status: string) => {
            if (status === 'OK' && results && results.length > 0 && results[0]?.geometry?.location) {
              const loc = results[0].geometry.location;
              const lat = typeof loc.lat === 'function' ? loc.lat() : Number(loc.lat);
              const lng = typeof loc.lng === 'function' ? loc.lng() : Number(loc.lng);
              if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
                const types: string[] = results[0].types || [];
                const isOnlyLocality =
                  results[0].geometry.location_type === 'APPROXIMATE' &&
                  types.every((t: string) => ['locality', 'political', 'administrative_area_level_1', 'administrative_area_level_2', 'country'].includes(t));

                if (!isOnlyLocality || !fallbackCoords) {
                  resolve({ lat, lng });
                  return;
                }
              }
            }
            resolve(fallbackCoords);
          });
        } catch {
          resolve(fallbackCoords);
        }
      };

      if (gMaps.places?.PlacesService && typeof document !== 'undefined') {
        try {
          if (!this.placesDummyDiv) {
            this.placesDummyDiv = document.createElement('div');
          }
          const placesService = new gMaps.places.PlacesService(this.placesDummyDiv);
          const placeReq: any = {
            query,
            fields: ['geometry', 'name', 'formatted_address']
          };
          if (biasCenter && biasCenter.lat && biasCenter.lng && gMaps.LatLng) {
            placeReq.locationBias = new gMaps.LatLng(biasCenter.lat, biasCenter.lng);
          }
          placesService.findPlaceFromQuery(placeReq, (places: any[], status: string) => {
            if (status === 'OK' && places && places.length > 0 && places[0]?.geometry?.location) {
              const loc = places[0].geometry.location;
              const lat = typeof loc.lat === 'function' ? loc.lat() : Number(loc.lat);
              const lng = typeof loc.lng === 'function' ? loc.lng() : Number(loc.lng);
              if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
                resolve({ lat, lng });
                return;
              }
            }
            tryGeocoder(null);
          });
          return;
        } catch {}
      }

      tryGeocoder(null);
    });
  }

  public buildEventGeocodeQuery(event: EventResult, fallbackLocationName?: string): string {
    if (!event) return '';
    const locName = (fallbackLocationName || '').trim();
    const rawPlace = (event.place || '').replace(/\s+-\s+/g, ', ').trim();
    const rawAddress = (event.address || '').trim();

    const invalidPlaceholders = new Set([
      'nearby', 'nearby spot', 'local area', 'local', 'area', 'tbd', 'n/a', 'na', 'none', 'somewhere nearby', 'location', 'anywhere'
    ]);

    const hasValidPlace = rawPlace.length > 2 && !invalidPlaceholders.has(rawPlace.toLowerCase());
    const hasSpecificAddress =
      rawAddress.length > 3 &&
      !invalidPlaceholders.has(rawAddress.toLowerCase()) &&
      rawAddress.toLowerCase() !== locName.toLowerCase();

    const parts: string[] = [];
    if (hasValidPlace) {
      parts.push(rawPlace);
    }
    if (hasSpecificAddress && (!hasValidPlace || !rawPlace.toLowerCase().includes(rawAddress.toLowerCase()))) {
      if (hasValidPlace && rawAddress.toLowerCase().includes(rawPlace.toLowerCase())) {
        parts.length = 0;
      }
      parts.push(rawAddress);
    }

    let combined = parts.join(', ').trim();
    if (!combined && event.title) {
      combined = event.title.trim();
    }

    if (locName && combined && !combined.toLowerCase().includes(locName.toLowerCase().split(',')[0].trim())) {
      combined = `${combined}, ${locName}`;
    } else if (!combined && locName) {
      combined = locName;
    }

    return combined;
  }

  public resolveEventCoordinates(
    event: EventResult,
    locationName?: string,
    biasCenter?: { lat: number; lng: number }
  ): Promise<{ lat: number; lng: number } | null> {
    if (!event) return Promise.resolve(null);
    const query = this.buildEventGeocodeQuery(event, locationName);
    if (!query) return Promise.resolve(null);

    const cached = this.getCachedGoogleCoords(query);
    if (cached) {
      event.lat = cached.lat;
      event.lng = cached.lng;
      return Promise.resolve(cached);
    }

    return this.resolveCoordsFromGoogle(query, biasCenter).then((coords) => {
      if (coords) {
        event.lat = coords.lat;
        event.lng = coords.lng;
      }
      return coords;
    });
  }

  public enrichEventsWithGoogleCoords(
    events: EventResult[],
    locationName?: string,
    biasCenter?: { lat: number; lng: number },
    onUpdate?: () => void
  ): void {
    if (!Array.isArray(events) || events.length === 0) return;

    let updatedSync = false;
    const pendingPromises: Promise<any>[] = [];

    events.forEach((ev) => {
      const query = this.buildEventGeocodeQuery(ev, locationName);
      if (!query) return;

      const cached = this.getCachedGoogleCoords(query);
      if (cached) {
        if (ev.lat !== cached.lat || ev.lng !== cached.lng) {
          ev.lat = cached.lat;
          ev.lng = cached.lng;
          updatedSync = true;
        }
        return;
      }

      const p = this.resolveCoordsFromGoogle(query, biasCenter).then((coords) => {
        if (coords && (ev.lat !== coords.lat || ev.lng !== coords.lng)) {
          ev.lat = coords.lat;
          ev.lng = coords.lng;
          if (onUpdate) {
            onUpdate();
          }
        }
      });
      pendingPromises.push(p);
    });

    if (updatedSync && onUpdate) {
      onUpdate();
    }
  }

  public initInteractions(userId?: string | number) {
    // 1. Read from localStorage for instant offline access
    try {
      const savedRaw = localStorage.getItem('a8_saved_ai_events');
      if (savedRaw) {
        this.savedEvents = JSON.parse(savedRaw) || [];
        this.savedEventsCount$.next(this.savedEvents.length);
      }
      const trashedRaw = localStorage.getItem('a8_trashed_ai_events');
      if (trashedRaw) {
        const arr = JSON.parse(trashedRaw) || [];
        this.trashedEventIds = new Set(arr);
        this.trashedEventsCount$.next(this.trashedEventIds.size);
      }
      const trashedMapRaw = localStorage.getItem('a8_trashed_ai_events_map');
      if (trashedMapRaw) {
        const parsed = JSON.parse(trashedMapRaw) || {};
        Object.keys(parsed).forEach(k => this.trashedEventsMap.set(k, parsed[k]));
      }
    } catch {}

    // 2. Sync from backend if user is authenticated
    const url = `${environment.apiUrl}api/v1/event-interactions/`;
    this.http.get<any>(url).pipe(catchError(() => of([]))).subscribe({
      next: (res) => {
        const items = res?.results || res || [];
        if (Array.isArray(items)) {
          const backendSaved: EventResult[] = [];
          items.forEach(item => {
            if (item.action === 'saved' && item.event_data) {
              backendSaved.push(item.event_data);
            } else if (item.action === 'trashed' && item.event_id) {
              this.trashedEventIds.add(item.event_id);
              if (item.event_data) {
                this.trashedEventsMap.set(item.event_id, item.event_data);
              }
            }
          });

          if (backendSaved.length > 0) {
            const existingIds = new Set(this.savedEvents.map(e => e.id));
            backendSaved.forEach(e => {
              if (!existingIds.has(e.id)) {
                this.savedEvents.push(e);
              }
            });
            this.savedEventsCount$.next(this.savedEvents.length);
            try {
              localStorage.setItem('a8_saved_ai_events', JSON.stringify(this.savedEvents));
            } catch {}
          }

          this.trashedEventsCount$.next(this.trashedEventIds.size);
          try {
            localStorage.setItem('a8_trashed_ai_events', JSON.stringify(Array.from(this.trashedEventIds)));
            const mapObj: any = {};
            this.trashedEventsMap.forEach((v, k) => { mapObj[k] = v; });
            localStorage.setItem('a8_trashed_ai_events_map', JSON.stringify(mapObj));
          } catch {}
        }
      }
    });
  }

  public isSaved(eventId: string): boolean {
    return this.savedEvents.some(e => e.id === eventId);
  }

  public isTrashed(eventId: string): boolean {
    return this.trashedEventIds.has(eventId);
  }

  public getSavedEvents(): EventResult[] {
    return this.savedEvents;
  }

  public getTrashedEvents(): EventResult[] {
    return Array.from(this.trashedEventsMap.values());
  }

  public getTrashedEventIds(): Set<string> {
    return this.trashedEventIds;
  }

  public toggleSaveEvent(event: EventResult): boolean {
    const isCurrentlySaved = this.isSaved(event.id);
    const url = `${environment.apiUrl}api/v1/event-interactions/`;

    if (isCurrentlySaved) {
      // Unsave
      this.savedEvents = this.savedEvents.filter(e => e.id !== event.id);
      this.savedEventsCount$.next(this.savedEvents.length);
      try {
        localStorage.setItem('a8_saved_ai_events', JSON.stringify(this.savedEvents));
      } catch {}

      this.http.post(`${url}remove_interaction/`, { event_id: event.id, action: 'saved' })
        .pipe(catchError(() => of(null)))
        .subscribe();
      return false;
    } else {
      // Save
      this.savedEvents.unshift(event);
      this.savedEventsCount$.next(this.savedEvents.length);
      try {
        localStorage.setItem('a8_saved_ai_events', JSON.stringify(this.savedEvents));
      } catch {}

      this.http.post(url, { event_id: event.id, action: 'saved', event_data: event })
        .pipe(catchError(() => of(null)))
        .subscribe();
      return true;
    }
  }

  public trashEvent(event: EventResult) {
    this.trashedEventIds.add(event.id);
    this.trashedEventsMap.set(event.id, event);
    this.trashedEventsCount$.next(this.trashedEventIds.size);

    // If it was saved, remove from saved
    if (this.isSaved(event.id)) {
      this.savedEvents = this.savedEvents.filter(e => e.id !== event.id);
      this.savedEventsCount$.next(this.savedEvents.length);
      try {
        localStorage.setItem('a8_saved_ai_events', JSON.stringify(this.savedEvents));
      } catch {}
    }

    try {
      localStorage.setItem('a8_trashed_ai_events', JSON.stringify(Array.from(this.trashedEventIds)));
      const mapObj: any = {};
      this.trashedEventsMap.forEach((v, k) => { mapObj[k] = v; });
      localStorage.setItem('a8_trashed_ai_events_map', JSON.stringify(mapObj));
    } catch {}

    const url = `${environment.apiUrl}api/v1/event-interactions/`;
    this.http.post(url, { event_id: event.id, action: 'trashed', event_data: event })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  public restoreTrashedEvent(eventId: string) {
    this.trashedEventIds.delete(eventId);
    this.trashedEventsMap.delete(eventId);
    this.trashedEventsCount$.next(this.trashedEventIds.size);

    try {
      localStorage.setItem('a8_trashed_ai_events', JSON.stringify(Array.from(this.trashedEventIds)));
      const mapObj: any = {};
      this.trashedEventsMap.forEach((v, k) => { mapObj[k] = v; });
      localStorage.setItem('a8_trashed_ai_events_map', JSON.stringify(mapObj));
    } catch {}

    const url = `${environment.apiUrl}api/v1/event-interactions/remove_interaction/`;
    this.http.post(url, { event_id: eventId, action: 'trashed' })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private aiCache = new Map<string, { timestamp: number; data: EventResult[] }>();
  private readonly CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days (monthly) for ongoing activities
  private readonly POPULAR_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days (monthly) for popular activities
  private popularCache = new Map<string, { timestamp: number; data: PopularActivitiesResult }>();
  private inFlightPopularRequests = new Map<string, Observable<PopularActivitiesResult>>();
  private inFlightRequests = new Map<string, Observable<EventResult[]>>();
  private preloadedLocations = new Set<string>();

  private readonly DEFAULT_POPULAR_ACTIVITIES: PopularActivitiesResult = {
    creative: ['Painting', 'Photography', 'Pottery', 'Acoustic Jam', 'Dance'],
    entertainment: ['Live Music', 'Board Games', 'Comedy Show', 'Trivia Night', 'Food & Dining'],
    exercise: ['Volleyball', 'Hiking', 'Rock Climbing', 'Yoga', 'Pickleball', 'Biking'],
    all: [
      'Volleyball', 'Hiking', 'Live Music', 'Coffee & Tea', 'Board Games',
      'Rock Climbing', 'Yoga', 'Photography', 'Pickleball', 'Biking',
      'Painting', 'Comedy Show', 'Running', 'Pottery', 'Trivia Night', 'Dance'
    ]
  };

  private getCacheKey(params: NearbyActivitiesParams): string {
    const loc = (params.locationName || `${params.lat?.toFixed(2)}_${params.lng?.toFixed(2)}`).toLowerCase().trim();
    const cat = params.category || 'all';
    const q = (params.searchQuery || '').toLowerCase().trim();
    const acts = (params.activities && params.activities.length > 0)
      ? params.activities.slice().sort().join(',').toLowerCase()
      : (params.scope || 'all');
    return `a8_ai_cache_v5_${loc}_${cat}_${q}_${acts}`;
  }

  private getFromCache(key: string): EventResult[] | null {
    // 1. In-memory fast cache
    if (this.aiCache.has(key)) {
      const entry = this.aiCache.get(key)!;
      if (Date.now() - entry.timestamp < this.CACHE_TTL_MS && entry.data?.length > 0) {
        return entry.data;
      }
    }

    // 2. Persistent localStorage cache
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const entry = JSON.parse(stored);
        if (Date.now() - entry.timestamp < this.CACHE_TTL_MS && Array.isArray(entry.data) && entry.data.length > 0) {
          const hasOnlySyntheticFallbacks = entry.data.every((ev: EventResult) => typeof ev.id === 'string' && ev.id.startsWith('fb-'));
          if (hasOnlySyntheticFallbacks) {
            return null;
          }
          entry.data.forEach((ev: EventResult, idx: number) => {
            if (!ev.photos || !ev.photos.length || ev.photos[0].includes('photo-1511795409834-ef04bbd61622')) {
              ev.photos = this.resolvePhotosForEvent(ev, idx);
            }
          });
          this.aiCache.set(key, entry);
          return entry.data;
        }
      }
    } catch {}

    return null;
  }

  private saveToCache(key: string, data: EventResult[], append: boolean = false) {
    if (!data || data.length === 0) return;
    let combined = data;
    if (append) {
      const existing = this.getFromCache(key) || [];
      const existingIds = new Set(existing.map(e => e.id));
      const existingTitles = new Set(existing.map(e => (e.title || '').toLowerCase()));
      const newItems = data.filter(d => !existingIds.has(d.id) && !existingTitles.has((d.title || '').toLowerCase()));
      combined = [...existing, ...newItems];
    }
    const entry = { timestamp: Date.now(), data: combined };
    this.aiCache.set(key, entry);
    try {
      localStorage.setItem(key, JSON.stringify(entry));
    } catch {}
  }

  getPopularActivities(params?: { locationName?: string; lat?: number; lng?: number; forceRefresh?: boolean }): Observable<PopularActivitiesResult> {
    const locName = (params?.locationName || '').trim();
    const cacheKey = `a8_popular_acts_v3_${(locName || 'default').toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
    const forceRefresh = Boolean(params?.forceRefresh);

    if (!forceRefresh) {
      // 1. Fast in-memory check (<1ms)
      if (this.popularCache.has(cacheKey)) {
        const entry = this.popularCache.get(cacheKey)!;
        if (Date.now() - entry.timestamp < this.POPULAR_CACHE_TTL_MS && entry.data?.all?.length > 0) {
          return of(entry.data);
        }
      }

      // 2. Fast persistent localStorage check (30-day TTL)
      try {
        const stored = localStorage.getItem(cacheKey);
        if (stored) {
          const entry = JSON.parse(stored);
          if (Date.now() - entry.timestamp < this.POPULAR_CACHE_TTL_MS && entry.data?.all?.length > 0) {
            this.popularCache.set(cacheKey, entry);
            return of(entry.data);
          }
        }
      } catch {}
    }

    // 3. Deduplicate in-flight requests
    if (this.inFlightPopularRequests.has(cacheKey)) {
      return this.inFlightPopularRequests.get(cacheKey)!;
    }

    // 4. Fast backend call to database cache (1 month) or Gemini
    const url = `${environment.apiUrl}api/v1/ai-events/popular-activities/?location_name=${encodeURIComponent(locName)}`;
    const req$ = this.http.get<any>(url).pipe(
      map((resp) => {
        if (resp && resp.activities && Array.isArray(resp.activities.all) && resp.activities.all.length > 0) {
          const result: PopularActivitiesResult = {
            creative: Array.isArray(resp.activities.creative) ? resp.activities.creative : [],
            entertainment: Array.isArray(resp.activities.entertainment) ? resp.activities.entertainment : [],
            exercise: Array.isArray(resp.activities.exercise) ? resp.activities.exercise : [],
            all: resp.activities.all
          };
          const cacheEntry = { timestamp: Date.now(), data: result };
          this.popularCache.set(cacheKey, cacheEntry);
          try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
          } catch {}
          return result;
        }
        return this.getFallbackPopularActivities(cacheKey);
      }),
      catchError(() => {
        return of(this.getFallbackPopularActivities(cacheKey));
      }),
      finalize(() => {
        this.inFlightPopularRequests.delete(cacheKey);
      }),
      shareReplay(1)
    );

    this.inFlightPopularRequests.set(cacheKey, req$);
    return req$;
  }

  private getFallbackPopularActivities(cacheKey: string): PopularActivitiesResult {
    try {
      const stored = localStorage.getItem(cacheKey);
      if (stored) {
        const entry = JSON.parse(stored);
        if (entry.data?.all?.length > 0) {
          return entry.data;
        }
      }
    } catch {}
    return this.DEFAULT_POPULAR_ACTIVITIES;
  }

  preloadLocationActivities(params?: { lat?: number; lng?: number; locationName?: string; forceRefresh?: boolean }): Observable<EventResult[]> {
    let lat = params?.lat;
    let lng = params?.lng;
    let locationName = params?.locationName;

    // Check localStorage fallbacks if missing
    if ((!lat || !lng || isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) || !locationName) {
      try {
        const userLocRaw = localStorage.getItem('a8_user_location');
        if (userLocRaw) {
          const parsed = JSON.parse(userLocRaw);
          if (!locationName) {
            locationName = parsed.name || (parsed.city && parsed.state ? `${parsed.city}, ${parsed.state}` : parsed.place || parsed.city);
          }
          if ((!lat || !lng || isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) && parsed.location?.coordinates) {
            lng = Number(parsed.location.coordinates[0]);
            lat = Number(parsed.location.coordinates[1]);
          }
        }
        if (!locationName || (!lat && !lng)) {
          const pubLocRaw = localStorage.getItem('a8_public_location');
          if (pubLocRaw) {
            const parsed = JSON.parse(pubLocRaw);
            if (!locationName && parsed.locationName) locationName = parsed.locationName;
            if ((!lat || !lng || isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) && parsed.lat && parsed.lng) {
              lat = Number(parsed.lat);
              lng = Number(parsed.lng);
            }
          }
        }
      } catch {}
    }

    if (!locationName && (!lat || !lng || (lat === 0 && lng === 0))) {
      return of([]);
    }

    const locKey = (locationName || `${lat?.toFixed(2)}_${lng?.toFixed(2)}`).toLowerCase().trim();
    if (this.preloadedLocations.has(locKey)) {
      return of([]);
    }
    this.preloadedLocations.add(locKey);

    return this.getPopularActivities({ locationName, lat, lng }).pipe(
      map(() => []),
      catchError(() => of([]))
    );
  }

  getNearbyActivities(params: NearbyActivitiesParams, bypassCache: boolean = false): Observable<EventResult[]> {
    const limit = params.limit || 8;
    const cacheKey = this.getCacheKey(params);
    const locName = params.locationName || '';
    const cat = params.category || 'all';
    const biasCenter = params.lat && params.lng ? { lat: params.lat, lng: params.lng } : undefined;

    if (!bypassCache) {
      // 1. Fast in-memory / local cache check
      const cached = this.getFromCache(cacheKey);
      if (cached && cached.length > 0) {
        this.enrichEventsWithGoogleCoords(cached, locName, biasCenter);
        return of(cached);
      }

      // 2. Fast in-flight request deduplication
      if (this.inFlightRequests.has(cacheKey)) {
        return this.inFlightRequests.get(cacheKey)!;
      }

      const dbQueryUrl = `${environment.apiUrl}api/v1/ai-events/?query_key=${encodeURIComponent(cacheKey)}&location_name=${encodeURIComponent(locName)}&category=${encodeURIComponent(cat)}`;

      const req$ = this.http.get<any>(dbQueryUrl).pipe(
        switchMap((dbResp) => {
          if (dbResp && dbResp.cached && Array.isArray(dbResp.results) && dbResp.results.length > 0) {
            const dbResults = this.mapDbEventsToResults(dbResp.results, params, limit);
            if (dbResults.length > 0) {
              this.enrichEventsWithGoogleCoords(dbResults, locName, biasCenter);
              this.saveToCache(cacheKey, dbResults, false);
              return of(dbResults);
            }
          }
          return this.fetchFromGeminiAndSave(params, cacheKey, limit, false, dbResp?.results);
        }),
        catchError(() => {
          return this.fetchFromGeminiAndSave(params, cacheKey, limit, false);
        }),
        finalize(() => {
          this.inFlightRequests.delete(cacheKey);
        }),
        shareReplay(1)
      );

      this.inFlightRequests.set(cacheKey, req$);
      return req$;
    }

    return this.fetchFromGeminiAndSave(params, cacheKey, limit, true);
  }

  private mapDbEventsToResults(items: any[], params: NearbyActivitiesParams, limit: number): EventResult[] {
    return items.slice(0, limit).map((ev: any, idx: number) => {
      const combinedText = `${ev.title || ''} ${ev.place || ''} ${ev.description || ''} ${ev.callout || ''} ${ev.eventDetails || ev.event_details || ''}`;
      const lowerText = combinedText.toLowerCase();
      const timeStr = ev.time || (ev.time_of_day && ev.time_of_day.includes('•') ? ev.time_of_day.split('•')[1].trim() : (ev.time_of_day && ev.time_of_day.includes(':') ? ev.time_of_day : ''));
      const dateStr = ev.date || (ev.datetime ? String(ev.datetime).split('T')[0] : '');

      const hasScheduledKeywords = /\b(workshop|class|session|trivia|show|concert|live music|market|mixer|open mic|jam|tour|meetup|festival|night)\b/i.test(combinedText);
      const hasTimeInDetails = /\b\d{1,2}(:\d{2})?\s*(am|pm)\b/i.test(`${timeStr} ${ev.eventDetails || ev.event_details || ''} ${ev.callout || ''}`);

      const inferredExact = Boolean(timeStr && timeStr.includes(':')) || hasTimeInDetails || Boolean(dateStr && hasScheduledKeywords);
      const inferredUpcoming = Boolean((dateStr || hasTimeInDetails) && (Boolean(timeStr && timeStr.includes(':')) || hasScheduledKeywords));

      const isAlcoholVenue = /\b(bar|brewery|brewing|taphouse|taproom|winery|wine|pub|tavern|saloon|nightclub|happy hour|cocktails|lounge)\b/i.test(lowerText);
      const rawFamily = Boolean(ev.familyFriendly ?? ev.family_friendly);
      const rawSober = Boolean(ev.sober);

      const res: EventResult = {
        id: String(ev.id || `ai_ev_${idx}`),
        title: ev.title || 'Local Activity',
        place: ev.place || params.locationName || 'Local Area',
        address: ev.address || '',
        description: ev.description || '',
        callout: ev.callout || '✨ Curated with Gemini',
        category: ev.category || params.category || 'entertainment',
        price: typeof ev.price === 'number' ? ev.price : Number(ev.price) || 0,
        distanceMiles: typeof ev.distanceMiles === 'number' ? ev.distanceMiles : (typeof ev.distance_miles === 'number' ? ev.distance_miles : 2.5),
        travelMinutes: typeof ev.travelMinutes === 'number' ? ev.travelMinutes : (typeof ev.travel_minutes === 'number' ? ev.travel_minutes : 8),
        commitmentLevel: ev.commitmentLevel || ev.commitment_level || 'moderate',
        duration: ev.duration || '1-2 hrs',
        timeOfDay: (ev.time_of_day && ev.time_of_day.includes('•') ? ev.time_of_day.split('•')[0].trim() : (ev.timeOfDay || ev.time_of_day || 'Anytime')),
        date: dateStr,
        time: timeStr,
        eventDetails: ev.eventDetails || ev.event_details || ev.callout || '',
        hasExactDetails: Boolean(ev.hasExactDetails ?? ev.has_exact_details ?? inferredExact),
        isUpcomingEvent: Boolean(ev.isUpcomingEvent ?? ev.is_upcoming_event ?? inferredUpcoming),
        petFriendly: Boolean(ev.petFriendly ?? ev.pet_friendly),
        familyFriendly: isAlcoholVenue ? false : rawFamily,
        sober: isAlcoholVenue ? false : rawSober,
        rating: Number(ev.rating) || 4.8,
        lat: typeof ev.lat === 'number' && !isNaN(ev.lat) && ev.lat !== 0 ? ev.lat : undefined,
        lng: typeof ev.lng === 'number' && !isNaN(ev.lng) && ev.lng !== 0 ? ev.lng : undefined,
        sourceUrl: (ev.sourceUrl || ev.source_url || '').startsWith('http') && !((ev.sourceUrl || ev.source_url || '').includes('google.com/search')) ? (ev.sourceUrl || ev.source_url) : '',
        mapsUrl: ev.mapsUrl || ev.maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((ev.place || ev.title) + ' ' + (params.locationName || ''))}`,
        searchUrl: ev.searchUrl || ev.search_url || `https://www.google.com/search?q=${encodeURIComponent((ev.title || '') + ' ' + (ev.place || '') + ' ' + (params.locationName || ''))}`,
        photos: Array.isArray(ev.photos) && ev.photos.length > 0 ? ev.photos : [],
        tags: Array.isArray(ev.tags) ? ev.tags : [],
        source: 'ai'
      };
      if (!res.photos || res.photos.length === 0) {
        res.photos = this.resolvePhotosForEvent(res, idx);
      }
      const query = this.buildEventGeocodeQuery(res, params.locationName);
      const cachedCoords = query ? this.getCachedGoogleCoords(query) : null;
      if (cachedCoords) {
        res.lat = cachedCoords.lat;
        res.lng = cachedCoords.lng;
      }
      return res;
    });
  }

  private fetchFromGeminiAndSave(
    params: NearbyActivitiesParams,
    cacheKey: string,
    limit: number,
    bypassCache: boolean,
    staleDbItems?: any[]
  ): Observable<EventResult[]> {
    const prompt = this.buildNearbyPrompt(params, limit);
    const locName = params.locationName || '';
    const biasCenter = params.lat && params.lng ? { lat: params.lat, lng: params.lng } : undefined;

    const payload = {
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.75,
        topP: 0.9,
        maxOutputTokens: 4096,
        responseMimeType: 'application/json',
        responseSchema: {
          type: 'ARRAY',
          items: {
            type: 'OBJECT',
            properties: {
              id: { type: 'STRING' },
              title: { type: 'STRING' },
              place: { type: 'STRING' },
              address: { type: 'STRING' },
              description: { type: 'STRING' },
              callout: { type: 'STRING' },
              category: { type: 'STRING', enum: ['creative', 'entertainment', 'exercise'] },
              price: { type: 'NUMBER' },
              distanceMiles: { type: 'NUMBER' },
              travelMinutes: { type: 'NUMBER' },
              commitmentLevel: { type: 'STRING', enum: ['easy', 'moderate', 'high'] },
              duration: { type: 'STRING' },
              timeOfDay: { type: 'STRING', enum: ['Morning', 'Afternoon', 'Evening'] },
              date: { type: 'STRING' },
              time: { type: 'STRING' },
              eventDetails: { type: 'STRING' },
              hasExactDetails: { type: 'BOOLEAN' },
              isUpcomingEvent: { type: 'BOOLEAN' },
              petFriendly: { type: 'BOOLEAN' },
              familyFriendly: { type: 'BOOLEAN' },
              sober: { type: 'BOOLEAN' },
              rating: { type: 'NUMBER' },
              lat: { type: 'NUMBER' },
              lng: { type: 'NUMBER' },
              sourceUrl: { type: 'STRING' },
              tags: { type: 'ARRAY', items: { type: 'STRING' } }
            },
            required: [
              'id', 'title', 'place', 'address', 'description', 'callout', 'category', 'price',
              'distanceMiles', 'travelMinutes', 'duration', 'timeOfDay', 'date', 'time', 'eventDetails',
              'hasExactDetails', 'isUpcomingEvent', 'petFriendly', 'familyFriendly', 'sober',
              'sourceUrl', 'lat', 'lng', 'tags'
            ]
          }
        }
      },
    };

    const resolveFallbackOrDb = (): Observable<EventResult[]> => {
      if (Array.isArray(staleDbItems) && staleDbItems.length > 0) {
        const mapped = this.mapDbEventsToResults(staleDbItems, params, limit);
        this.enrichEventsWithGoogleCoords(mapped, locName, biasCenter);
        this.saveToCache(cacheKey, mapped, false);
        return of(mapped);
      }

      if (locName) {
        const cat = params.category || 'all';
        const locUrl = `${environment.apiUrl}api/v1/ai-events/?location_name=${encodeURIComponent(locName)}&category=${encodeURIComponent(cat)}`;
        return this.http.get<any>(locUrl).pipe(
          map((locResp) => {
            if (locResp && Array.isArray(locResp.results) && locResp.results.length > 0) {
              let items = locResp.results;
              if (params.activities && params.activities.length > 0) {
                const actSet = params.activities.map(a => a.toLowerCase().trim());
                const filtered = items.filter((ev: any) => {
                  const text = `${ev.title || ''} ${ev.description || ''} ${(ev.tags || []).join(' ')}`.toLowerCase();
                  return actSet.some(act => text.includes(act));
                });
                if (filtered.length > 0) {
                  items = filtered;
                }
              }
              const mapped = this.mapDbEventsToResults(items, params, limit);
              this.enrichEventsWithGoogleCoords(mapped, locName, biasCenter);
              this.saveToCache(cacheKey, mapped, false);
              return mapped;
            }
            const fallback = this.getFallbackNearbyActivities(params, limit);
            this.enrichEventsWithGoogleCoords(fallback, locName, biasCenter);
            return fallback;
          }),
          catchError(() => {
            const fallback = this.getFallbackNearbyActivities(params, limit);
            this.enrichEventsWithGoogleCoords(fallback, locName, biasCenter);
            return of(fallback);
          })
        );
      }

      const fallback = this.getFallbackNearbyActivities(params, limit);
      this.enrichEventsWithGoogleCoords(fallback, locName, biasCenter);
      return of(fallback);
    };

    return this.http.post<any>(this.geminiBackendUrl, { payload, prompt }).pipe(
      switchMap((resp) => {
        const results = this.parseNearbyGeminiResponse(resp, params, limit);
        if (results.length > 0) {
          this.enrichEventsWithGoogleCoords(results, locName, biasCenter);
          this.saveToCache(cacheKey, results, bypassCache);
          this.http.post<any>(`${environment.apiUrl}api/v1/ai-events/save-batch/`, {
            query_key: cacheKey,
            question: prompt,
            location_name: params.locationName || '',
            category: params.category || 'all',
            events: results
          }).subscribe({
            next: () => {},
            error: (err) => console.warn('Could not save AI events to database:', err)
          });
          return of(results);
        }
        return resolveFallbackOrDb();
      }),
      catchError((err) => {
        console.warn('Gemini backend request failed, resolving from DB or fallback:', err);
        return resolveFallbackOrDb();
      })
    );
  }

  getFallbackNearbyActivities(params: NearbyActivitiesParams, limit: number): EventResult[] {
    const locName = params.locationName || 'Local Area';
    const reqCat = params.category;
    const baseSeeds = [
      { title: 'Golden Hour Happy Hour Crawl', place: `Downtown Patio District - ${locName}`, tags: ['Happy Hour', 'Social', 'Drinks'], cat: 'entertainment' },
      { title: 'Sunset Nature Photo Walk', place: `Scenic Nature Trail - ${locName}`, tags: ['Photo Walk', 'Sunset', 'Nature'], cat: 'creative' },
      { title: 'Night Market + Street Art Ride', place: `Warehouse Arts District - ${locName}`, tags: ['Biking', 'Street Art', 'Night Market'], cat: 'exercise' },
      { title: 'Rooftop Board Game Mixer', place: `Old Market Square - ${locName}`, tags: ['Board Games', 'Rooftop', 'Mixer'], cat: 'entertainment' },
      { title: 'Food Truck & Live Music Loop', place: `Community Plaza - ${locName}`, tags: ['Food Trucks', 'Live Music', 'Social'], cat: 'entertainment' },
      { title: 'Social Pickleball & Open Play', place: `Community Sports Park - ${locName}`, tags: ['Pickleball', 'Sports', 'Fitness'], cat: 'exercise' },
      { title: 'Coffee + Creative Sketch Jam', place: `Artisan Coffee Roasters - ${locName}`, tags: ['Coffee', 'Sketching', 'Art'], cat: 'creative' },
      { title: 'Scenic Hiking & Ridge Trail', place: `Scenic Mountain Trail - ${locName}`, tags: ['Hiking', 'Trails', 'Outdoors'], cat: 'exercise' },
      { title: 'Secret Mural Photo Quest', place: `Arts District Walk - ${locName}`, tags: ['Street Art', 'Photography', 'Explore'], cat: 'creative' },
      { title: 'Morning Outdoor Yoga & Stretch', place: `Lakeside Park - ${locName}`, tags: ['Yoga', 'Outdoors', 'Morning'], cat: 'exercise' },
      { title: 'Trivia Team-Up Night', place: `Local Taphouse - ${locName}`, tags: ['Trivia', 'Games', 'Pub'], cat: 'entertainment' },
      { title: 'Scenic Bike Loop & Exploration', place: `River Greenway - ${locName}`, tags: ['Biking', 'Adventure', 'Trails'], cat: 'exercise' },
      { title: 'Live Jazz & Acoustic Patio Hang', place: `Village Lounge - ${locName}`, tags: ['Live Jazz', 'Music', 'Patio'], cat: 'entertainment' },
      { title: 'Pottery & Ceramics Workshop', place: `Local Clay Studio - ${locName}`, tags: ['Pottery', 'Crafts', 'Ceramics'], cat: 'creative' },
      { title: 'Social Rock Climbing Session', place: `Bouldering & Climbing Gym - ${locName}`, tags: ['Rock Climbing', 'Fitness', 'Adventure'], cat: 'exercise' },
      { title: 'Stand-Up Comedy & Showcase', place: `Downtown Comedy Stage - ${locName}`, tags: ['Comedy Show', 'Entertainment', 'Social'], cat: 'entertainment' },
      { title: 'Watercolor & Painting Social', place: `Community Arts Studio - ${locName}`, tags: ['Painting', 'Arts', 'Social'], cat: 'creative' },
      { title: 'Beach Volleyball & Open Games', place: `Community Beach Courts - ${locName}`, tags: ['Volleyball', 'Sports', 'Outdoors'], cat: 'exercise' },
      { title: 'Acoustic Jam & Open Mic', place: `Coffee & Music House - ${locName}`, tags: ['Acoustic Jam', 'Music', 'Creative'], cat: 'creative' },
      { title: 'Casual Running & Trail Jog', place: `Park Perimeter Trail - ${locName}`, tags: ['Running', 'Fitness', 'Outdoors'], cat: 'exercise' },
    ];

    let pool = baseSeeds;
    if (reqCat) {
      const catFiltered = baseSeeds.filter(s => s.cat === reqCat);
      if (catFiltered.length > 0) pool = catFiltered;
    }

    if (params.activities && params.activities.length > 0) {
      const actItems = params.activities.map((act) => {
        let actCat: 'creative' | 'entertainment' | 'exercise' = reqCat || 'entertainment';
        if (!reqCat) {
          const actLower = act.toLowerCase();
          if (['paint', 'photo', 'potter', 'dance', 'art', 'sketch', 'craft', 'write', 'jam', 'music'].some(k => actLower.includes(k))) {
            actCat = 'creative';
          } else if (['hike', 'bike', 'climb', 'yoga', 'volleyball', 'run', 'swim', 'pickleball', 'sport', 'trail'].some(k => actLower.includes(k))) {
            actCat = 'exercise';
          } else {
            actCat = 'entertainment';
          }
        }
        return {
          title: `${act} with Locals`,
          place: `${locName} ${act} Spot`,
          tags: [act, actCat.charAt(0).toUpperCase() + actCat.slice(1)],
          cat: actCat
        };
      });
      pool = [...actItems, ...pool];
    }

    const callouts = [
      'Popular with locals this week',
      'Beginners and first-timers welcome',
      'Great atmosphere & friendly community',
      'Free admission & casual vibe',
      'Top-rated spot nearby',
    ];

    return Array.from({ length: limit }).map((_, index) => {
      const item = pool[index % pool.length];
      const price = index % 4 === 0 ? 0 : 10 + (index % 5) * 5;
      const catVal = item.cat as 'creative' | 'entertainment' | 'exercise';
      const photos = this.resolvePhotosForEvent({ title: item.title, place: item.place, tags: item.tags, category: catVal }, index);

      return {
        id: `fb-${catVal}-${index}-${Math.random().toString(36).slice(2, 7)}`,
        title: item.title,
        place: item.place,
        address: `${locName}`,
        description: `Enjoy ${item.tags[0] || 'local activities'} in ${locName}. A great pick for meeting people and trying something fun.`,
        callout: callouts[index % callouts.length],
        category: catVal,
        photos,
        tags: item.tags,
        distanceMiles: Number((1.2 + (index % 7) * 0.9).toFixed(1)),
        travelMinutes: 8 + (index % 6) * 4,
        commitmentLevel: (['easy', 'moderate', 'high'][index % 3]) as 'easy' | 'moderate' | 'high',
        price,
        petFriendly: index % 3 !== 0,
        familyFriendly: index % 2 === 0,
        sober: index % 4 === 0,
        rating: Number((4.6 + (index % 4) * 0.1).toFixed(1)),
        source: 'ai',
        sourceUrl: `https://www.google.com/search?q=${encodeURIComponent(item.title + ' ' + locName)}`,
        mapsUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.place + ' ' + locName)}`,
        searchUrl: `https://www.google.com/search?q=${encodeURIComponent(item.title + ' ' + locName)}`,
        timeOfDay: ['Morning', 'Afternoon', 'Evening', 'Tonight'][index % 4],
        duration: '1-2 hrs',
        lat: undefined,
        lng: undefined,
      };
    });
  }

  getRecommendations(filters: EventsFilters, paging: EventsPaging): Observable<EventResult[]> {
    const prompt = this.buildPrompt(filters, paging.limit);

    const payload = {
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.9,
        topP: 0.95,
        maxOutputTokens: 4096,
        responseMimeType: 'application/json',
      },
    };

    return this.http.post<any>(this.geminiBackendUrl, { payload, prompt }).pipe(
      map((resp) => this.parseGeminiResponse(resp, paging)),
      catchError(() => of(this.popularSeedResults(paging.offset, paging.limit)))
    );
  }

  private buildPrompt(filters: EventsFilters, count: number): string {
    const when = filters.when === 'furtherOut' ? 'further out (next week+)' : filters.when;
    const times = filters.time.join(', ') || 'any time';
    const categories = filters.categorySlugs.join(', ') || 'any category';
    const travel = filters.locationModes.map((m) => {
      if (m === 'walk30m') { return 'walkable within 30 minutes'; }
      if (m === 'drive30m') { return 'drivable within 30 minutes'; }
      return 'any location';
    }).join(' or ');

    const duration = filters.duration[0] || '60m';
    const priceRange = filters.priceMax === 0
      ? 'free only'
      : filters.priceMin > 0
        ? `$${filters.priceMin} to $${filters.priceMax}`
        : `up to $${filters.priceMax}`;

    const social = filters.rollWith.includes('group')
      ? 'group friendly'
      : filters.rollWith.includes('soloMeetPeople')
        ? 'solo but want to meet people'
        : 'solo in solidarity';

    return `You are a local events and activities recommender. Suggest ${count} fun, exciting, real-world things to do based on these preferences:

- When: ${when}
- Time of day: ${times}
- Categories: ${categories}
- Travel: ${travel}
- Duration: ${duration}
- Price: ${priceRange}
- Social: ${social}

For each event return a JSON object with these exact fields:
- title (string, creative name)
- place (string, realistic venue/area name)
- description (string, 1-2 sentences, vivid)
- callout (string, an exciting highlight like "happy hour special" or "rare wildlife sighting nearby")
- distanceMiles (number, 0.5-15)
- travelMinutes (number, 3-40)
- commitmentLevel ("easy" or "moderate" or "high")
- price (number, 0 for free)
- petFriendly (boolean)
- rating (number, 3.5-5.0)
- photoKeywords (array of 3 strings, each a short Unsplash-friendly search term that visually represents this specific event, e.g. ["rooftop bar sunset", "craft cocktails", "city skyline night"]. Make each keyword unique and specific to the event.)

Return a JSON array of ${count} objects. No markdown, no explanation, just the JSON array.`;
  }

  private parseGeminiResponse(resp: any, paging: EventsPaging): EventResult[] {
    try {
      const text = resp?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!text) {
        return this.popularSeedResults(paging.offset, paging.limit);
      }

      const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
      const parsed = JSON.parse(cleaned);

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return this.popularSeedResults(paging.offset, paging.limit);
      }

      const results = parsed.map((item: any) => this.mapEvent(item));

      if (results.length >= paging.limit) {
        return results.slice(0, paging.limit);
      }

      return results.concat(
        this.popularSeedResults(paging.offset + results.length, paging.limit - results.length)
      );
    } catch {
      return this.popularSeedResults(paging.offset, paging.limit);
    }
  }

  private readonly TOPIC_PHOTOS: { keywords: string[]; photos: string[] }[] = [
    // 0. Pottery, Ceramics, Clay & Wheel Throwing
    {
      keywords: ['pottery', 'pottery wheel', 'ceramic', 'ceramics', 'clay', 'wheel throwing', 'potter', 'throwing wheel', 'glaze', 'kiln', 'sculpting clay'],
      photos: [
        'photo-1565193566173-7a0ee3dbe261',
        'photo-1578749556568-bc2c40e68b61',
        'photo-1590736969955-71cc94801759',
        'photo-1534447677768-be436bb09401',
        'photo-1618221195710-dd6b41faaea6',
        'photo-1522075469751-3a6694fb2f61',
      ]
    },
    // 1. Climbing & Bouldering
    {
      keywords: ['bouldering', 'climb', 'rock climbing', 'belay', 'crag', 'mountaineering', 'climbing gym'],
      photos: [
        'photo-1522163182402-834f871fd851',
        'photo-1564769625905-50e93615e769',
        'photo-1583623025817-d180a2221d0a',
        'photo-1502126324834-38f8e02d7160',
        'photo-1601224748193-d34fdec56ef9',
        'photo-1544717305-2782549b5136',
      ]
    },
    // 2. Hiking, Trails & Mountains
    {
      keywords: ['hike', 'hiking', 'trail', 'mountain', 'trek', 'backpack', 'nature', 'summit', 'peak', 'canyon', 'waterfall', 'forest', 'state park', 'scenic walk'],
      photos: [
        'photo-1551632811-561732d1e306',
        'photo-1464822759023-fed622ff2c3b',
        'photo-1506744038136-46273834b3fb',
        'photo-1448375240586-882707db888b',
        'photo-1470071459604-3b5ec3a7fe05',
        'photo-1426604966848-d7adac402bff',
      ]
    },
    // 3. Running, Jogging & 5K
    {
      keywords: ['run', 'running', 'marathon', 'jog', 'jogging', '5k', '10k', 'sprint', 'track', 'running club'],
      photos: [
        'photo-1476480862126-209bfaa8edc8',
        'photo-1502680390469-be75c86b636f',
        'photo-1530549387789-4c1017266635',
        'photo-1486218119243-13883505764c',
        'photo-1452626038306-9aae5e071dd3',
      ]
    },
    // 4. Cycling & Biking
    {
      keywords: ['bike', 'biking', 'cycl', 'cycling', 'bicycle', 'mtb', 'mountain bike', 'velodrome', 'gravel ride'],
      photos: [
        'photo-1485965120184-e220f721d03e',
        'photo-1544197150-b99a580bb7a8',
        'photo-1507035895480-2b3156c31fc8',
        'photo-1517649763962-0c623266ddc0',
        'photo-1501386761578-eac5c94b800a',
      ]
    },
    // 5. Yoga, Pilates & Mindfulness
    {
      keywords: ['yoga', 'pilates', 'meditat', 'mindful', 'sound bath', 'stretch', 'namaste', 'breathwork', 'zen'],
      photos: [
        'photo-1545205597-3d9d02c29597',
        'photo-1506126613408-eca07ce68773',
        'photo-1518611012118-696072aa579a',
        'photo-1575052814086-f385e2e2ad1b',
        'photo-1599447421416-3414500d18a5',
      ]
    },
    // 6. Fitness, Gym, CrossFit & Weights
    {
      keywords: ['gym', 'workout', 'fitness', 'crossfit', 'weightlifting', 'weights', 'lifting weights', 'kettlebell', 'boxing', 'martial arts', 'hiit', 'bootcamp', 'powerlifting', 'pickleball', 'tennis'],
      photos: [
        'photo-1534438327276-14e5300c3a48',
        'photo-1517838277536-f5f99be501cd',
        'photo-1549060279-7e168fcee0c2',
        'photo-1581009146145-b5ef050c2e1e',
        'photo-1574680096145-d05b474e2155',
      ]
    },
    // 7. Water Sports, Swimming, Surfing & Beach
    {
      keywords: ['surf', 'surfing', 'paddleboard', 'sup', 'kayak', 'kayaking', 'canoe', 'swim', 'swimming', 'beach', 'lake', 'ocean', 'snork', 'scuba', 'sailing', 'boat', 'water sports'],
      photos: [
        'photo-1502680390469-be75c86b636f',
        'photo-1507525428034-b723cf961d3e',
        'photo-1544551763-46a013bb70d5',
        'photo-1473186578172-c141e6798cf4',
        'photo-1508873696983-2df5293cb325',
      ]
    },
    // 8. Live Music, Concerts & Jazz
    {
      keywords: ['music', 'concert', 'band', 'guitar', 'jazz', 'acoustic', 'gig', 'festival', 'live show', 'orchestra', 'choir', 'dj', 'vinyl', 'jam session', 'blues', 'rock', 'singing'],
      photos: [
        'photo-1511671782779-c97d3d27a1d4',
        'photo-1470225620780-dba8ba36b745',
        'photo-1514525253161-7a46d19cd819',
        'photo-1511192336575-5a79af67a629',
        'photo-1465847899084-d164df4dedc6',
      ]
    },
    // 9. Art, Painting, Drawing & Crafts
    {
      keywords: ['art', 'paint', 'painting', 'sculpt', 'sketch', 'draw', 'drawing', 'craft', 'crafts', 'gallery', 'museum', 'exhibit', 'exhibition', 'workshop', 'diy', 'makerspace', 'studio', 'mural'],
      photos: [
        'photo-1460661419200-fd4357fe772e',
        'photo-1579783900882-c0d3dad7b119',
        'photo-1513364776144-60967b0f800f',
        'photo-1578301978693-85fa9c0320b9',
        'photo-1455390582262-044cdead277a',
      ]
    },
    // 10. Coffee, Tea & Cafes
    {
      keywords: ['coffee', 'cafe', 'espresso', 'latte', 'cappuccino', 'tea', 'matcha', 'roaster', 'barista', 'bakery cafe'],
      photos: [
        'photo-1501339847302-ac426a4a7cbb',
        'photo-1495474472287-4d71bcdd2085',
        'photo-1442512595331-e89e73853f31',
        'photo-1514432324607-a09d9b4aefdd',
      ]
    },
    // 11. Food, Dining, Pizza, Bakery & Cooking
    {
      keywords: ['food', 'cook', 'cooking', 'chef', 'bakery', 'baking', 'pizza', 'taco', 'sushi', 'bbq', 'restaurant', 'dinner', 'brunch', 'pastry', 'culinary', 'farmers market', 'food truck', 'tapas', 'tasting'],
      photos: [
        'photo-1555396273-367ea4eb4db5',
        'photo-1556910103-1c02745aae4d',
        'photo-1513104890138-7c749659a591',
        'photo-1504674900247-0877df9cc836',
        'photo-1509440159596-0249088772ff',
      ]
    },
    // 12. Beer, Brewery, Wine & Cocktails
    {
      keywords: ['beer', 'brewery', 'brewpub', 'wine', 'vineyard', 'winery', 'cocktail', 'cocktails', 'bar', 'pub', 'speakeasy', 'lounge', 'cider', 'distillery', 'taproom', 'drinks', 'happy hour'],
      photos: [
        'photo-1514933651103-005eec06c04b',
        'photo-1551024709-8f23befc6f87',
        'photo-1538488881523-294da0c87b63',
        'photo-1510812431401-41d2bd2722f3',
        'photo-1572116469696-31de0f17cc34',
      ]
    },
    // 13. Comedy, Theater & Performing Arts
    {
      keywords: ['comedy', 'standup', 'improv', 'theater', 'theatre', 'play', 'drama', 'musical', 'actor', 'cinema', 'movie', 'film', 'screening'],
      photos: [
        'photo-1585699324551-f6c309eedeca',
        'photo-1507676184212-d03ab07a01bf',
        'photo-1514306191717-452ec28c7814',
        'photo-1489599849927-2ee91cede3ba',
      ]
    },
    // 14. Games, Trivia, Arcade & Bowling
    {
      keywords: ['game', 'games', 'trivia', 'board game', 'board games', 'arcade', 'pinball', 'bowling', 'billiards', 'pool table', 'escape room', 'vr', 'gaming', 'chess', 'tabletop'],
      photos: [
        'photo-1610890716171-6b1bb98ffd09',
        'photo-1511512578047-dfb367046420',
        'photo-1543083477-4f785aeafaa9',
        'photo-1538510899802-550ced6638ae',
        'photo-1529699211952-734e80c4d42b',
      ]
    },
    // 15. Dance, Salsa & Swing
    {
      keywords: ['dance', 'dancing', 'salsa', 'tango', 'swing', 'ballroom', 'bachata', 'hip hop', 'ballet', 'zumba', 'dance class'],
      photos: [
        'photo-1508700115892-45ecd05ae2ad',
        'photo-1547153760-18fc86324498',
        'photo-1518834107812-67b0b7c58434',
        'photo-1504609773096-104ff2c73ba4',
      ]
    },
    // 16. Pets & Animals
    {
      keywords: ['dog', 'dogs', 'puppy', 'cat', 'cats', 'pet', 'pets', 'petting', 'animal', 'horse', 'equestrian', 'wildlife', 'zoo', 'aquarium', 'dog park'],
      photos: [
        'photo-1548199973-03cce0bbc87b',
        'photo-1587300003388-59208cc962cb',
        'photo-1535268647677-300dbf3d78d1',
        'photo-1553284965-83fd3e82fa5a',
      ]
    },
    // 17. Gardening, Plants & Botanical
    {
      keywords: ['garden', 'gardening', 'plant', 'plants', 'botanical', 'flower', 'flowers', 'greenhouse', 'nursery', 'orchard', 'succulent', 'flora'],
      photos: [
        'photo-1585320806297-9794b3e4eeae',
        'photo-1466692476868-aef1dfb1e735',
        'photo-1416879595882-3373a0480b5b',
        'photo-1530595467537-0b5996c41f2d',
      ]
    },
    // 18. Books, Writing & Literature
    {
      keywords: ['book', 'books', 'reading', 'library', 'bookstore', 'poetry', 'writing', 'literature', 'journal', 'book club', 'storytelling'],
      photos: [
        'photo-1521587760476-6c12a4b040da',
        'photo-1497633762265-9d179a990aa6',
        'photo-1455390582262-044cdead277a',
        'photo-1481627834876-b7833e8f5570',
      ]
    },
    // 19. Stargazing, Campfire & Sunsets
    {
      keywords: ['stargazing', 'astronomy', 'camp', 'camping', 'campfire', 'sunset', 'sunrise', 'bonfire', 'night sky', 'stars'],
      photos: [
        'photo-1506744038136-46273834b3fb',
        'photo-1510312305653-8ed496efae75',
        'photo-1519681393784-d120267933ba',
        'photo-1470240731273-7821a6eeb6bd',
      ]
    },
  ];

  private readonly CATEGORY_PHOTOS: Record<'creative' | 'entertainment' | 'exercise', string[]> = {
    exercise: [
      'photo-1517838277536-f5f99be501cd',
      'photo-1551632811-561732d1e306',
      'photo-1544197150-b99a580bb7a8',
      'photo-1545205597-3d9d02c29597',
      'photo-1522163182402-834f871fd851',
      'photo-1476480862126-209bfaa8edc8',
      'photo-1544551763-46a013bb70d5',
      'photo-1534438327276-14e5300c3a48',
    ],
    creative: [
      'photo-1565193566173-7a0ee3dbe261',
      'photo-1460661419200-fd4357fe772e',
      'photo-1579783900882-c0d3dad7b119',
      'photo-1521587760476-6c12a4b040da',
      'photo-1585320806297-9794b3e4eeae',
      'photo-1513364776144-60967b0f800f',
      'photo-1455390582262-044cdead277a',
      'photo-1578301978693-85fa9c0320b9',
    ],
    entertainment: [
      'photo-1511671782779-c97d3d27a1d4',
      'photo-1514525253161-7a46d19cd819',
      'photo-1555396273-367ea4eb4db5',
      'photo-1514933651103-005eec06c04b',
      'photo-1610890716171-6b1bb98ffd09',
      'photo-1585699324551-f6c309eedeca',
      'photo-1507676184212-d03ab07a01bf',
      'photo-1527529482837-4698179dc6ce',
    ],
  };

  private matchKeyword(text: string, keyword: string): boolean {
    if (!text || !keyword) return false;
    const t = text.toLowerCase();
    const kw = keyword.toLowerCase().trim();
    if (t === kw) return true;
    if (kw.includes(' ')) {
      return t.includes(kw);
    }
    const regex = new RegExp(`(^|[^a-z0-9])${kw}([^a-z0-9]|$)`, 'i');
    return regex.test(t);
  }

  private getPhotoUrl(photoId: string, querySlug?: string): string {
    const slugParam = querySlug ? `&sig=${encodeURIComponent(querySlug)}` : '';
    return `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=900&q=75${slugParam}`;
  }

  public resolvePhotosForEvent(item: any, seedIndex: number = 0): string[] {
    // 1. First, check if there is a real photo from the source website (e.g. squarespace, cdn, or venue domain)
    let sourcePhoto: string | null = null;
    const possibleSource = item.sourcePhotoUrl || item.photoUrl || (Array.isArray(item.photos) && item.photos[0] && !item.photos[0].includes('unsplash.com') ? item.photos[0] : null);
    if (possibleSource && typeof possibleSource === 'string' && possibleSource.startsWith('http') && !possibleSource.includes('unsplash.com')) {
      sourcePhoto = possibleSource;
    }

    // 2. Extract search terms: title, tags, place
    const title = (item.title || '').trim();
    const cleanTitleSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    let firstTag = '';
    if (Array.isArray(item.tags) && item.tags.length > 0) {
      firstTag = String(item.tags[0]).replace(/^#/, '').trim().toLowerCase();
    }
    if (!firstTag && title) {
      firstTag = title.toLowerCase();
    }
    const allTagsText = Array.isArray(item.tags) ? item.tags.join(' ') : '';
    const searchCombined = `${title} ${firstTag} ${allTagsText} ${item.place || ''}`.toLowerCase();

    // Deterministic hash based on activity, title and first tag for rotation
    const hashString = `${item.id || ''}_${firstTag}_${title}_${seedIndex}`;
    let hash = 0;
    for (let i = 0; i < hashString.length; i++) {
      hash = (hash << 5) - hash + hashString.charCodeAt(i);
      hash |= 0;
    }
    const absHash = Math.abs(hash);

    // 3. Match against topic photos using accurate word boundary keyword matching
    let unsplashPair: string[] = [];
    for (const topic of this.TOPIC_PHOTOS) {
      const match = topic.keywords.some(kw => this.matchKeyword(searchCombined, kw));
      if (match && topic.photos.length >= 2) {
        const idx1 = absHash % topic.photos.length;
        const idx2 = (idx1 + 1) % topic.photos.length;
        unsplashPair = [
          this.getPhotoUrl(topic.photos[idx1], cleanTitleSlug),
          this.getPhotoUrl(topic.photos[idx2], cleanTitleSlug),
        ];
        break;
      }
    }

    // 4. Fallback to category pool if no specific topic matched
    if (unsplashPair.length === 0) {
      const cat = (item.category === 'creative' || item.category === 'exercise' || item.category === 'entertainment')
        ? item.category
        : 'creative';
      const catPool = this.CATEGORY_PHOTOS[cat] || this.CATEGORY_PHOTOS.creative;
      const idx1 = absHash % catPool.length;
      const idx2 = (idx1 + 1) % catPool.length;
      unsplashPair = [
        this.getPhotoUrl(catPool[idx1], cleanTitleSlug),
        this.getPhotoUrl(catPool[idx2], cleanTitleSlug),
      ];
    }

    // 5. If source website photo is present, prioritize it first followed by the Unsplash fallbacks
    if (sourcePhoto) {
      return [sourcePhoto, ...unsplashPair];
    }

    return unsplashPair;
  }

  private mapEvent(item: any): EventResult {
    const photos = this.resolvePhotosForEvent(item);

    return {
      id: `ai-${Math.random().toString(36).slice(2, 9)}`,
      title: item.title || 'Surprise Experience',
      place: item.place || item.locationName || 'Nearby',
      description: item.description || 'A memorable way to break up your routine.',
      photos,
      distanceMiles: Number(item.distanceMiles || item.distance || 2.4),
      travelMinutes: Number(item.travelMinutes || item.travelTime || 11),
      commitmentLevel: item.commitmentLevel || 'easy',
      price: Number(item.price || 0),
      petFriendly: Boolean(item.petFriendly),
      rating: Number(item.rating || 4.4),
      callout: item.callout || 'Trending with locals tonight',
      source: 'ai',
    };
  }

  private popularSeedResults(offset: number, limit: number): EventResult[] {
    const seedData = [
      { title: 'Golden Hour Happy Hour Crawl', place: 'Downtown Patio District', tags: ['Happy Hour', 'Social', 'Drinks'], cat: 'entertainment' },
      { title: 'Sunset Nature Photo Walk', place: 'Riverfront Greenway', tags: ['Photo Walk', 'Sunset', 'Nature'], cat: 'creative' },
      { title: 'Night Market + Street Art Ride', place: 'Warehouse Arts Block', tags: ['Biking', 'Street Art', 'Night Market'], cat: 'exercise' },
      { title: 'Rooftop Board Game Mixer', place: 'Old Market Square', tags: ['Board Games', 'Rooftop', 'Mixer'], cat: 'entertainment' },
      { title: 'Food Truck Treasure Loop', place: 'Downtown Patio District', tags: ['Food Trucks', 'Dining', 'Outdoor'], cat: 'entertainment' },
      { title: 'Beginner Pickleball Pop-up', place: 'Riverfront Greenway', tags: ['Pickleball', 'Sports', 'Fitness'], cat: 'exercise' },
      { title: 'Coffee + Creative Sketch Jam', place: 'Warehouse Arts Block', tags: ['Coffee', 'Sketching', 'Art'], cat: 'creative' },
      { title: 'Dog Friendly Park Social', place: 'Riverfront Greenway', tags: ['Dog Park', 'Pets', 'Outdoors'], cat: 'entertainment' },
      { title: 'Secret Mural Photo Quest', place: 'Warehouse Arts Block', tags: ['Street Art', 'Photography', 'Explore'], cat: 'creative' },
      { title: 'Morning Lake Stretch', place: 'Lake Loop', tags: ['Yoga', 'Lake', 'Morning'], cat: 'exercise' },
      { title: 'Trivia Team-Up Night', place: 'Old Market Square', tags: ['Trivia', 'Games', 'Pub'], cat: 'entertainment' },
      { title: 'Tiny Adventure Bike Route', place: 'Lake Loop', tags: ['Cycling', 'Adventure', 'Trails'], cat: 'exercise' },
      { title: 'Live Jazz Patio Hang', place: 'Downtown Patio District', tags: ['Live Jazz', 'Music', 'Patio'], cat: 'entertainment' },
      { title: 'Farmers Market Taste Tour', place: 'Old Market Square', tags: ['Farmers Market', 'Food', 'Local'], cat: 'creative' },
      { title: 'Community Garden Volunteer Hour', place: 'Riverfront Greenway', tags: ['Gardening', 'Community', 'Plants'], cat: 'creative' },
      { title: 'Indie Movie Night', place: 'Warehouse Arts Block', tags: ['Cinema', 'Film', 'Night Out'], cat: 'entertainment' },
      { title: 'Kayak Social Paddle', place: 'Lake Loop', tags: ['Kayaking', 'Water Sports', 'Lake'], cat: 'exercise' },
      { title: 'Bookstore Puzzle Race', place: 'Old Market Square', tags: ['Bookstore', 'Games', 'Reading'], cat: 'creative' },
      { title: 'Sunrise Walk Club', place: 'Riverfront Greenway', tags: ['Walking', 'Sunrise', 'Outdoors'], cat: 'exercise' },
      { title: 'Local Pottery & Ceramic Workshop', place: 'Warehouse Arts Block', tags: ['Pottery', 'Crafts', 'Ceramics'], cat: 'creative' },
    ];

    const callouts = [
      '2-for-1 mocktails and snacks until 7pm',
      'Beautiful fox recently sighted along the west trail',
      'Limited-time neon installation this weekend',
      'Easy icebreaker games included',
      'Popular with first-timers this week',
    ];

    return Array.from({ length: limit }).map((_, index) => {
      const seedIndex = offset + index;
      const data = seedData[seedIndex % seedData.length];
      const price = seedIndex % 5 === 0 ? 0 : (seedIndex % 10) * 10;

      return {
        id: `seed-event-${seedIndex}`,
        title: data.title,
        place: data.place,
        description: 'A lively local pick with enough structure to feel easy and enough surprise to feel memorable.',
        photos: this.resolvePhotosForEvent({ title: data.title, place: data.place, tags: data.tags, category: data.cat }, seedIndex),
        tags: data.tags,
        category: data.cat as 'creative' | 'entertainment' | 'exercise',
        distanceMiles: Number((1.1 + (seedIndex % 9) * 0.7).toFixed(1)),
        travelMinutes: 6 + (seedIndex % 8) * 4,
        commitmentLevel: ['easy', 'moderate', 'high'][seedIndex % 3] as 'easy' | 'moderate' | 'high',
        price,
        petFriendly: seedIndex % 3 !== 0,
        rating: 4.2 + (seedIndex % 7) / 10,
        callout: callouts[seedIndex % callouts.length],
        source: 'popularSeed',
      };
    });
  }

  private buildNearbyPrompt(params: NearbyActivitiesParams, count: number): string {
    const locParts: string[] = [];
    if (params.locationName) {
      locParts.push(params.locationName);
    }
    if (params.lat && params.lng && !isNaN(params.lat) && !isNaN(params.lng)) {
      locParts.push(`Coordinates: ${params.lat.toFixed(4)}, ${params.lng.toFixed(4)}`);
    }
    const locationStr = locParts.length > 0 ? locParts.join(' - ') : 'the user\'s local area';
    const categoryFocus = params.category
      ? `Focus strictly on activities in the '${params.category}' category (${params.category === 'creative' ? 'Arts, Crafts, Music, Painting, Pottery, Writing, Dance' : params.category === 'entertainment' ? 'Concerts, Gatherings, Social, Comedy, Board Games, Trivia, Nightlife' : 'Adventure, Sports, Workouts, Hiking, Biking, Yoga, Climbing'}).`
      : 'Include a balanced mix across Creative (Arts, Crafts, Music), Entertainment (Concerts, Gatherings, Social), and Exercise (Adventure, Sports, Workouts).';
    const searchFilter = params.searchQuery ? `Only suggest activities matching or related to: "${params.searchQuery}".` : '';
    const activitiesFocus = (params.activities && params.activities.length > 0)
      ? `CRITICAL PRIORITY: The user is specifically interested in these activities: [${params.activities.join(', ')}]. You MUST tailor the ${count} suggested things to do to directly match or be related to these activities (e.g. classes, workshops, open sessions, social games, or top scenic spots for: ${params.activities.join(', ')}).`
      : '';
    const excludeFilter = (params.excludeTitles && params.excludeTitles.length > 0)
      ? `DO NOT repeat any of these already-shown activities or venues: [${params.excludeTitles.slice(-24).join(', ')}]. Suggest ${count} completely different events and spots.`
      : '';

    const todayStr = new Date().toISOString().slice(0, 10);
    return `Local events & activities assistant for Activity8. Date: ${todayStr}.
Suggest ${count} real-world events and activities within 15 miles of ${locationStr} at verifiable Google Maps venues.
${categoryFocus}
${activitiesFocus}
${searchFilter}
${excludeFilter}

RULES:
1. Event Mix: Aim for >=50% real scheduled events (classes, workshops, live music, trivia, markets, comedy, group sessions -> "isUpcomingEvent": true, "hasExactDetails": true) and the rest top local activity suggestions ("isUpcomingEvent": false).
2. Strict Booleans (DO NOT default to true):
   - "sober": DEFAULT FALSE. True ONLY if 100% alcohol-free (e.g. yoga studio, trail hike, sober club, library, ceramic studio). Any bar, brewery, restaurant, lounge, concert venue, comedy club, or mixer MUST be false.
   - "familyFriendly": True ONLY if genuinely kid/family friendly. False for bars, breweries, nightlife, 18+/21+ venues, or adult mixers.
   - "petFriendly": True ONLY if dogs/pets are allowed (dog trails/beaches/patios). False for indoor studios, gyms, museums, theaters.
3. Field Guidelines:
   - "place": Exact real venue/park/business name in ${locationStr}.
   - "address": Real street address in ${locationStr}.
   - "description": Detailed 2-3 sentences on what participants experience, atmosphere, and what makes it special.
   - "callout": Short punchy highlight (e.g. "Live acoustic set starts at 7 PM").
   - "date": "YYYY-MM-DD" on or after ${todayStr} (next occurrence for weekly events).
   - "time": Exact start time or window (e.g. "7:00 PM", "10:00 AM - 12:00 PM"). Never use "Afternoon" or "Evening".
   - "eventDetails": Actionable schedule/details (e.g. "Every Thursday • Sign-ups 6:30 PM, starts 7:00 PM").
   - "sourceUrl": Official venue/ticketing https:// URL (never google.com).
   - "tags": 2-3 tags; first tag MUST be the specific activity name.`;
  }

  private parseNearbyGeminiResponse(resp: any, params: NearbyActivitiesParams, limit: number): EventResult[] {
    try {
      const text = resp?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!text) {
        return [];
      }

      const parsed = this.safeParseJsonArray(text);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        return [];
      }

      const results = parsed.map((item: any, idx: number) => this.mapNearbyEvent(item, idx, params.category, params.locationName));
      return results.slice(0, limit);
    } catch (err) {
      console.warn('Failed to parse Gemini response:', err);
      return [];
    }
  }

  private safeParseJsonArray(rawText: string): any[] {
    if (!rawText) return [];
    const cleaned = rawText.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();

    // 1. Direct parse attempt
    try {
      const parsed = JSON.parse(cleaned);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // String was truncated or unclosed
    }

    // 2. Recovery: Find the last complete object "}" in a truncated JSON array
    try {
      const lastCloseBrace = cleaned.lastIndexOf('}');
      if (lastCloseBrace !== -1) {
        const repaired = cleaned.slice(0, lastCloseBrace + 1) + ']';
        const parsed = JSON.parse(repaired);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {
      // Continue to regex recovery
    }

    // 3. Fallback: Parse every individual valid JSON object
    try {
      const regex = /\{[\s\S]*?\}(?=\s*(?:,|\s*\]|$))/g;
      const matches = cleaned.match(regex);
      if (matches && matches.length > 0) {
        const items: any[] = [];
        for (const m of matches) {
          try {
            items.push(JSON.parse(m));
          } catch {}
        }
        if (items.length > 0) return items;
      }
    } catch {}

    return [];
  }

  private mapNearbyEvent(item: any, index: number, fallbackCat?: 'creative' | 'entertainment' | 'exercise' | null, locationName?: string): EventResult {
    const photos = this.resolvePhotosForEvent(item, index);

    const title = item.title || 'Local Discovery Experience';
    const place = item.place || item.locationName || 'Nearby Spot';
    const address = item.address || (item.place ? `${item.place}, ${locationName || ''}`.trim() : (locationName || ''));
    const validCat: 'creative' | 'entertainment' | 'exercise' =
      ['creative', 'entertainment', 'exercise'].includes(item.category)
        ? item.category
        : (fallbackCat || 'entertainment');

    const locSuffix = locationName ? ` ${locationName}` : '';
    const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(title + ' ' + (item.address || place) + locSuffix)}`;
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((item.address || place) + (locationName && !item.address?.includes(locationName) ? ', ' + locationName : ''))}`;

    const isRealSourceUrl = item.sourceUrl &&
      typeof item.sourceUrl === 'string' &&
      item.sourceUrl.trim().startsWith('http') &&
      !item.sourceUrl.includes('google.com/search') &&
      !item.sourceUrl.includes('google.com/maps');

    const realSourceUrl = isRealSourceUrl ? item.sourceUrl.trim() : '';

    const dateVal = item.date || (item.datetime ? String(item.datetime).split('T')[0] : '');
    const timeVal = item.time || (item.timeOfDay && item.timeOfDay.includes(':') ? item.timeOfDay : '');
    const rawTimeOfDay = item.timeOfDay || '';
    const cleanTimeOfDay = (rawTimeOfDay.includes('•') ? rawTimeOfDay.split('•')[0] : rawTimeOfDay).trim() || 'Evening';
    const eventDetails = item.eventDetails || item.callout || '';

    const combinedText = `${title} ${place} ${item.description || ''} ${item.callout || ''} ${eventDetails}`;
    const isAlcoholVenue = /\b(bar|brewery|brewing|taphouse|taproom|winery|wine|pub|tavern|saloon|nightclub|happy hour|cocktails|lounge)\b/i.test(combinedText);

    const lat = item.lat != null && !isNaN(Number(item.lat)) && Number(item.lat) !== 0 ? Number(item.lat) : undefined;
    const lng = item.lng != null && !isNaN(Number(item.lng)) && Number(item.lng) !== 0 ? Number(item.lng) : undefined;

    return {
      id: item.id ? `ai-${item.id}` : `ai-${Math.random().toString(36).slice(2, 9)}`,
      title,
      place,
      address,
      description: item.description || 'Discover a memorable local activity curated specifically for you.',
      callout: item.callout || 'Popular with locals this week',
      category: validCat,
      photos,
      distanceMiles: Number(item.distanceMiles || 2.5),
      travelMinutes: Number(item.travelMinutes || 12),
      commitmentLevel: item.commitmentLevel || 'easy',
      price: Number(item.price || 0),
      duration: item.duration || '01:30:00',
      timeOfDay: cleanTimeOfDay,
      date: dateVal,
      time: timeVal,
      eventDetails,
      hasExactDetails: Boolean(item.hasExactDetails ?? (Boolean(timeVal && timeVal.includes(':')) || Boolean(dateVal && !['anytime', 'all day', 'varies'].includes(String(timeVal || '').toLowerCase())))),
      isUpcomingEvent: Boolean(item.isUpcomingEvent ?? (Boolean(dateVal) && Boolean(timeVal && timeVal.includes(':')))),
      petFriendly: Boolean(item.petFriendly === true),
      familyFriendly: isAlcoholVenue ? false : Boolean(item.familyFriendly === true),
      sober: isAlcoholVenue ? false : Boolean(item.sober === true),
      rating: Number(item.rating || 4.7),
      source: 'ai',
      sourceUrl: realSourceUrl,
      sourcePhotoUrl: item.sourcePhotoUrl || item.photoUrl || '',
      mapsUrl,
      searchUrl,
      tags: Array.isArray(item.tags) ? item.tags : [validCat.charAt(0).toUpperCase() + validCat.slice(1), 'Local', 'Trending'],
      lat,
      lng,
    };
  }
}


